<template>
  <div class="rs-layout-app container-fluid h-100 m-0 p-0">
    <div class="d-flex h-100">
      <RsLayoutAppSidebar
        v-if="!isIFrame"
        ref="rsLayoutAppSidebarRef"
        sidebar-width="300px"
        @on-collapse-toggle="onSidebarCollapseToggle"
      >
        <template #navigation>
          <RsPartialNavigationTree
            :to="{ name: 'Dashboard' }"
            tree-top
            title="Dashboard"
            class="rs-text-white font-weight-normal mt-2 hide-dashboard-expand-icon"
            :class="navTreeSpacingClasses"
            icon="far fa-tachometer-fast rs-text-tertiary-medium-purple"
            active-icon="fas fa-tachometer-fast rs-text-primary"
            icon-size="lg"
            :variant="variant"
            :active="$route.path === '/'"
            no-expand
          />

          <RsPartialNavProperties
            :expanded="currentExpanded === 'properties'"
            :variant="variant"
            :class="navTreeSpacingClasses"
            @click.native="handleNavExpand('properties')"
          />
          <RsPartialNavWebsites
            :expanded="currentExpanded === 'websites'"
            :variant="variant"
            :class="navTreeSpacingClasses"
            @click.native="handleNavExpand('websites')"
          />
          <RsPartialNavListingManagement
            :expanded="currentExpanded === 'listing_management'"
            :variant="variant"
            :class="navTreeSpacingClasses"
            @click.native="handleNavExpand('listing_management')"
          />
          <RsPartialNavLeads
            :expanded="currentExpanded === 'leads'"
            :variant="variant"
            :class="navTreeSpacingClasses"
            @click.native="handleNavExpand('leads')"
          />
          <RsPartialNavTours
            :key="currentExpanded"
            :expanded="currentExpanded === 'tours'"
            :variant="variant"
            :class="navTreeSpacingClasses"
            @click.native="handleNavExpand('tours')"
          />

          <!-- BSTK Offers -->
          <RsPartialNavigationTree
            v-for="offer in bstkOffers"
            :key="offer.title"
            :to="hasBuildingStackConnection ? offer.bstkUrl : offer.to"
            tree-top
            :title="offer.title"
            class="rs-text-white font-weight-normal hide-dashboard-expand-icon bstk-credit"
            :class="navTreeSpacingClasses"
            :icon="offer.icon"
            :active-icon="offer.activeIcon"
            icon-size="lg"
            :variant="variant"
            :active="offer.active"
            :hide-expand-icon="true"
            no-expand
          >
            <template #beforeExpandIcon>
              <i
                v-if="hasBuildingStackConnection"
                class="icon-sm fa-light fa-square-arrow-up-right"
              />
              <i
                v-else
                class="icon-sm far fa-lock-alt rs-text-warning"
              />
            </template>
          </RsPartialNavigationTree>

          <RsPartialNavReports
            :expanded="currentExpanded === 'reports'"
            :variant="variant"
            :class="navTreeSpacingClasses"
            @click.native="handleNavExpand('reports')"
          />
          <RsPartialNavMarketInsights
            :expanded="currentExpanded === 'market_insights'"
            :variant="variant"
            :class="navTreeSpacingClasses"
            @click.native="handleNavExpand('market_insights')"
          />
          <RsPartialNavSuperadminSettings
            :expanded="currentExpanded === 'superadmin_settings'"
            :variant="variant"
            :class="navTreeSpacingClasses"
            @click.native="handleNavExpand('superadmin_settings')"
          />
        </template>
      </RsLayoutAppSidebar>
      <div
        class="main-content container-fluid p-0"
      >
        <div class="h-100 d-flex flex-column">
          <!-- <BAlert
            :show="$rsGlobals.$env.VUE_APP_SHOW_ALERT === '1'"
            variant="danger"
            dismissible
            class="mb-0 small px-3 py-2 border-none rounded-0"
          >
            <h4>
              {{ $rsGlobals.$env.VUE_APP_ALERT_TITLE }}
            </h4>
            <p class="mb-0">
              {{ $rsGlobals.$env.VUE_APP_ALERT_MESSAGE }}
            </p>
          </BAlert> -->

          <RsPartialAppHeader
            v-if="!$rsGlobals.$isIFrame"
            @on-modal-account-trigger="$emit('on-modal-account-trigger')"
            @on-logout-trigger="$emit('on-logout-trigger')"
          />

          <!-- <div v-if="isIFrame" class="py-2 px-4 d-flex header-page-title justify-content-end border-bottom">
            <RsButton
              icon="sign-out"
              @click="handleLogout"
            >
              IFrame Logout (POC Only)
            </RsButton>
          </div> -->

          <!--  App Page Content - ScrollBars -> RouterView -->
          <div class="flex-fill app-page-content container m-0 p-0 position-relative">
            <RsSpin v-show="$store.state.loading" class="rs-new-spin shadow" />
            <OverlayScrollbars
              id="base-os-bar"
              class="h-100"
              :class="scrollbarClass"
              :options="currentRouteMeta?.overlayScrollbarsOptions"
            >
              <RsPartialIFrameHeader
                v-if="isIFrame"
                @on-modal-account-trigger="$emit('on-modal-account-trigger')"
                @on-logout-trigger="$emit('on-logout-trigger')"
              />
              <RouterView :class="routerViewClass" />
            </OverlayScrollbars>
          </div>
          <!-- App Footer -->
          <RsPartialAppFooter />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import appMixin from '@/mixins/app-mixin';
import { routeStartsWith } from '@/shared/helpers/navigation-active-helpers';
import RsLayoutAppSidebar from '@/components/RsLayoutAppSidebar.vue';
import RsPartialAppHeader from '@/components/RsPartialAppHeader.vue';
import RsPartialAppFooter from '@/components/RsPartialAppFooter.vue';
import RsPartialNavigationTree, { NavigationTreeVariantType } from '@/components/RsPartialNavigationTree.vue';
import RsPartialNavProperties from '@/components/RsPartialNavProperties.vue';
import RsPartialNavListingManagement from './RsPartialNavListingManagement.vue';
import RsPartialNavWebsites from '@/components/RsPartialNavWebsites.vue';
import RsPartialNavLeads from '@/components/RsPartialNavLeads.vue';
import RsPartialNavTours from '@/components/RsPartialNavTours.vue';
import RsPartialNavReports from '@/components/RsPartialNavReports.vue';
import RsPartialNavMarketInsights from '@/components/RsPartialNavMarketInsights.vue';
import RsPartialNavSuperadminSettings from '@/components/RsPartialNavSuperadminSettings.vue';
import RsPartialIFrameHeader from './RsPartialIFrameHeader.vue';
import { getClientLogoURL } from '@/shared/helpers/get-client-logo-url';
import { hasBuildingStackConnection, hasSecuredForms } from '@/shared/helpers/user-access';
import users from '@/shared/services/users';

const BSTK_TENANT_SCR_URL = 'https://app.buildingstack.com/applications/creditCheck';
const BSTK_RENTAL_APP_URL = 'https://app.buildingstack.com/applications';
const BSTK_ONLINE_PAY_URL = 'https://app.buildingstack.com/accounting/leases/automaticPayments';

export default defineComponent({
  components: {
    RsLayoutAppSidebar,
    RsPartialAppFooter,
    RsPartialAppHeader,
    RsPartialNavWebsites,
    RsPartialNavLeads,
    RsPartialNavTours,
    RsPartialNavProperties,
    RsPartialNavSuperadminSettings,
    RsPartialNavReports,
    RsPartialNavMarketInsights,
    RsPartialNavigationTree,
    RsPartialNavListingManagement,
    RsPartialIFrameHeader,
  },

  mixins: [ appMixin ],

  data() {
    return {
      isSidebarCollapsed: false as boolean,
      accountSwitchTreeExpanded: false as boolean,
      isDropdownVisible: false as boolean,
      currentExpanded: '',
    };
  },

  computed: {
    hasBuildingStackConnection,
    hasSecuredForms,

    variant(): NavigationTreeVariantType {
      return !this.isSidebarCollapsed ? 'collapse' : 'tile_with_popout';
    },

    navTreeSpacingClasses(): Record<string, boolean> {
      return {
        'px-2 py-1': this.variant === 'collapse',
      };
    },

    currentRouteMeta() {
      return this.$route?.meta;
    },

    /**
     * Resolves the padding class applied to the main scrollable container based on
     * the current route's meta flags (iframeNav, noPagePadding).
     * Also applies the content background class when `useContentBg` is set.
     */
    scrollbarClass(): (string | Record<string, boolean>)[] {
      const meta = this.currentRouteMeta;
      let padding = 'p-3';

      if (meta?.iframeNav || meta?.noPagePadding) {
        padding = 'p-0';
      } else if (this.isIFrame && (this.backButtonConfig || this.$route?.meta?.hasIframeBackButton)) {
        padding = 'px-3 pb-3';
      }

      return [padding, { 'rs-bg-content': !!meta?.useContentBg }];
    },

    /**
     * Adds `p-3` padding to the router view when an iFrame nav bar is present and noPagePadding isn't set,
     * so that the page content is not flush against the nav bar.
     */
    routerViewClass(): string {
      const meta = this.currentRouteMeta;
      if (meta?.iframeNav && !meta?.noPagePadding) return 'p-3';
      return '';
    },

    isIFrame() {
      return this.$rsGlobals.$isIFrame;
    },

    topLevelRoutesPathMap(): Record<string, boolean> {
      return {
        properties: routeStartsWith('/properties'),
        leads: routeStartsWith('/leads'),
        listing_management: routeStartsWith('/syndication'),
        reports: routeStartsWith('/reporting') && !routeStartsWith('/reporting/market-insights'),
        market_insights: routeStartsWith('/reporting/market-insights'),
        superadmin_settings: routeStartsWith('/superadmin-account-settings'),
        tours: routeStartsWith('/tours'),
        websites: routeStartsWith('/websites') || routeStartsWith('/floorplan-navigator') || routeStartsWith('/unit-list-builder') || routeStartsWith('/property-search'),
      };
    },

    isCanadianClientAndAdmin(): boolean {
      return this.$rsGlobals.$userHasAccess('admin')
        && this.$rsGlobals.$currentClient?.city?.province?.country?.countryCode === 'CAN'
        && !this.$rsGlobals.$currentClient.parentId;
    },

    rentalApplicationsTo(): String | Record<string, string> {
      return {
        name: (() => {
          if (this.hasSecuredForms) {
            return 'BuildingStackRentalApplicationsPromoSF';
          }
          return 'BuildingStackRentalApplicationsPromo';
        })(),
      };
    },

    rentalApplicationsActive(): boolean {
      if (this.hasSecuredForms) {
        return this.$route.path === '/building-stack/rental-applications-promo-sf';
      }
      return this.$route.path === '/building-stack/rental-applications-promo';
    },

    bstkOffers() {
      if (!this.isCanadianClientAndAdmin || this.$rsGlobals.$currentUser?.getClientSettingValue('disable_bstk_promos') === '1') {
        return [];
      }

      const { rentalApplicationsTo, rentalApplicationsActive, $route } = this;

      return [
        {
          to: { name: 'BuildingStackCreditCheckPromo' },
          bstkUrl: BSTK_TENANT_SCR_URL,
          title: 'Tenant Screening',
          active: $route.path === '/building-stack/tenant-screening-promo',
          icon: 'far fa-user-check rs-text-tertiary-medium-purple',
          activeIcon: 'fas fa-user-check rs-text-primary',
        },
        {
          to: rentalApplicationsTo,
          bstkUrl: BSTK_RENTAL_APP_URL,
          title: 'Rental Applications',
          active: rentalApplicationsActive,
          icon: 'fa-regular fa-file-user rs-text-tertiary-medium-purple',
          activeIcon: 'fa-regular fa-file-user rs-text-primary',
        },
        {
          to: { name: 'BuildingStackOnlinePaymentsPromo' },
          bstkUrl: BSTK_ONLINE_PAY_URL,
          title: 'Online Payments',
          active: $route.path === '/building-stack/online-payments-promo',
          icon: 'fa-regular fa-credit-card rs-text-tertiary-medium-purple',
          activeIcon: 'fa-regular fa-credit-card rs-text-primary',
        },
      ];
    },
  },

  mounted() {
    this.currentExpanded = Object.keys(this.topLevelRoutesPathMap).find((t) => this.topLevelRoutesPathMap[t]) ?? '';
  },

  methods: {
    routeStartsWith,
    getClientLogoURL,

    onSidebarCollapseToggle(collapsed: boolean) {
      this.isSidebarCollapsed = collapsed;
      this.accountSwitchTreeExpanded = false;
    },

    handleLogoClicked() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },

    handleClickOutsideAvatarMenu() {
      if (this.isDropdownVisible) {
        this.isDropdownVisible = false;
      }
    },

    handleNavExpand(value: string) {
      this.currentExpanded = value;
    },

    handleLogout() {
      users.logout();
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables';

$icon-dims: 34px;
$sidebar-width: 260px;

html, body {
  height: 100%;
}

.hide-dashboard-expand-icon {
  .expand-icon {
    opacity: 0;
  }
}

.client-avatar {
  object-fit: fill;
  height: 42px;
  width: 42px;
  border-radius: 20px;
}

.rs-layout-app {
  .sidebar, .main-content {
    height: 100%;
  }

  .main-content {
    background: $white;

    .title-back-button {
      background: $primary-color;
      color: $white;
      height: $icon-dims;
      width: $icon-dims;
      border-radius: 50%;
      font-size: 1.25rem;
      i {
        line-height: inherit;
      }
    }

    .header-page-title {
      height: $navbarTopTitleHeight;
      background: $tertiary-muted;

      .title-icon {
        background: lighten($tertiary-muted, 35%);
        color: $dark-gray;
        border-radius: $global-radius;
        height: $icon-dims;
        width: $icon-dims;
        font-size: 1.25rem;
      }

      .customSVG{
        width: 80%;
        filter: invert(47%) sepia(4%) saturate(2188%) hue-rotate(194deg) brightness(94%) contrast(88%);
      }

      .title-offset-btn, .title-offset-btn .rs-button {
        color: $white;
        height: $icon-dims;
        width: $icon-dims;
        border-radius: 50%;
        font-size: 1.25rem;
        background: $secondary-color-dark;

        i {
          line-height: inherit;
        }

        &.resource-centre-button {
          background-color: $primary-color;
        }

        &.avatar-button, &.avatar-button .rs-button {
          background-color: transparent;
        }
      }

      .crumbs {
        font-size: 0.9rem;
        font-weight: normal;
      }

      .crumb-parent {
        font-size: 1.25rem;
      }

      .crumb-parent:hover, .crumbs:hover {
        color: $white;
      }
    }

    .child-nav-tabs {
      background-color: $tertiary-light-purple;
      border-bottom: 1px solid $tertiary-gray;

      .child-nav-title {
        font-size: 0.9rem;
      }

      .btn-burger {
        font-size: 18px;
        border-radius: $input-radius;
        transition: all ease 200ms;
        background: none;
        color: $dark-gray;
        outline: none;
        height: 34px;
        margin-top: 1.5px;
        margin-bottom: 1.5px;

        i {
          line-height: inherit;
        }

        &:hover {
          background: $gray-300;
        }

        &:active, &:focus {
          outline: none;
          border: none;
          box-shadow: none;
          color: $dark-gray;
        }
      }

      .dropdown-menu {
        background-color: $tertiary-light-purple;
      }

      .dropdown-item {
        color: $dark-gray;

        &:active {
          background: none;
          color: $dark-gray;
        }

        &.router-link-active {
          font-weight: bold;
        }
      }

      .list-group {
        .list-group-item:first-child {
          border-bottom-left-radius: 0;
        }

        .list-group-item {
          border: 0;
          min-width: 40px;
          background-color: inherit;

          a, span {
            color: $black;
            font-size: 14px;
            &.router-link-active {
              font-weight: bold;
              &:after {
                content: '';
                display: block;
                width: 100%;
                height: 4px;
                border-top-left-radius: 4px;
                border-top-right-radius: 4px;
                background: $secondary-color;
                position: absolute;
                bottom: 0;
                left: 0;
              }
            }
          }
        }
      }
    }

    .app-page-content {
      background-color: #F7F7F9;
      min-width:100%;

      .rs-settings {
        height: 100%;
      }
    }

    .footer-bar {
      .rs-button {
        position: static;
      }
      .rs-select {
        margin-bottom: 0;
      }
      .footer-text {
        opacity: 0.6 !important;
        font-size: 14px;
      }
    }
  }

  .sidebar {
    max-width: $sidebar-width;
    background: $secondary-color-dark;

    .os-scrollbar-handle {
      background:rgba(255, 255, 255, 0.2) !important;
    }

    .logo {
      width: 150px;
    }

    .slide-enter-active {
      -moz-transition-duration: 0.3s;
      -webkit-transition-duration: 0.3s;
      -o-transition-duration: 0.3s;
      transition-duration: 0.3s;
      -moz-transition-timing-function: ease;
      -webkit-transition-timing-function: ease;
      -o-transition-timing-function: ease;
      transition-timing-function: ease;
    }

    .slide-leave-active {
      -moz-transition-duration: 0.3s;
      -webkit-transition-duration: 0.3s;
      -o-transition-duration: 0.3s;
      transition-duration: 0.3s;
      -moz-transition-timing-function: ease;
      -webkit-transition-timing-function: ease;
      -o-transition-timing-function: ease;
      transition-timing-function: ease;
    }

    .slide-enter-to, .slide-leave {
      max-height: 300px;
      overflow: hidden;
    }

    .slide-enter, .slide-leave-to {
      overflow: hidden;
      max-height: 0;
    }

    .top-level-nav-item-avatar {
      width: 40px;
      max-width: 40px;
      min-width: 40px;
      height: 40px;
    }

    .top-level-nav-item {
      min-width: 0;
      flex: 1 1 auto;
      font-size: 16px;
      font-weight: bold;
      color: $white;
    }

    .list-group-item {
      background: transparent;

      .btn-accordion, a {
        font-size: 18px;
        font-weight: bold;
        color: $white;

        i {
          color: $extra-light-gray;
        }
      }

      a:hover, span:hover, span.router-link-active,
      a.router-link-active,
      .btn-accordion.router-link-active .btn-accordion-nav-text,
      .btn-accordion:hover .btn-accordion-nav-text {
        i {
          color: $primary-color;
        }
      }

      .list-group {
        .list-group-item {
          a, span {
            font-weight: lighter;
            font-size: 16px;
            padding-left: 48px !important;
            &:hover, &.router-link-active {
              padding-left: 42px !important;
              border-left: solid 6px $primary-color;
              font-weight: bold;
              background: rgba(255, 255, 255, 0.05);
            }
            &:hover {
              font-weight: lighter;
            }
            &.router-link-active:hover {
              font-weight: bold;
            }
          }
        }
      }
    }
  }

  .bstk-credit {
    .icon-sm {
      font-size: 100%;
    }
  }
}
</style>
